import {
  MESH_CATEGORIES, MESH_TYPES,
  type FileExportArray, type FileData, type FileIdx, type FileDataSchema, type MeshType,
  type ExportStatusDetail, type ExportProgressDetail, type ExportCancelDetail, type FileInfoArr,
} from '../../../data/data-interfaces.ts';
import { isMeshPart } from './_utils.ts';
import { buildZipExport, type ZipExportOptions } from './zipExport.ts';
import { startHistoryBatch, cancelHistoryBatch } from '../../history/undoHistory.ts';
import { saveFileAs, getFileSaveProvider } from '../../host/fileSave.ts';
import { internalData } from '../../../store/store.ts';
import { asApp, asConfigurator } from '../../../util/singletons.ts';
import { sendEv } from '../../../data/events.ts';

export interface ExportBuilderOptions {
  /** Default fileType for mesh parts (default: 'stl'). */
  preferredMeshType?: MeshType;
  /** ID used in as-export-status events so listeners can target a specific builder (default: 'default'). */
  exporterId?: string;
}

/** Dispatch an as-export-status event on window. Used by ExportBuilder and its plugins. */
export function dispatchExportStatus(detail: ExportStatusDetail): void {
  sendEv(window, 'as-export-status', { detail });
}

/** Dispatch an as-export-progress event on window. Emitted once per file inside resolve(). */
export function dispatchExportProgress(detail: ExportProgressDetail): void {
  sendEv(window, 'as-export-progress', { detail });
}

/**
 * Thrown when the user cancels an export. Callers that already report failures
 * should check isExportCancelled() first — a cancel is not an error.
 */
export class ExportCancelledError extends Error {
  constructor(message = 'Export cancelled') {
    super(message);
    this.name = 'ExportCancelledError';
  }
}

/** True if the error came from a user cancellation rather than a real failure. */
export function isExportCancelled(err: unknown): boolean {
  return err instanceof ExportCancelledError || (err as { name?: string } | null)?.name === 'ExportCancelledError';
}

/**
 * How long an export stays cancellable, measured from when it starts.
 *
 * An export that resolves faster than this would otherwise pass its point of no
 * return before anyone could reach the Cancel button — the progress panel would
 * appear and the data would be gone in the same instant. So the last step waits
 * out whatever is left of this window first (see awaitCancelGrace). The wait is
 * a real one, so a cancel arriving during it lands and takes effect.
 */
export const CANCEL_GRACE_PERIOD_MS = 1500;

/**
 * Ask the running export to stop. Fired by the progress modal's Cancel button.
 * An export can only stop between steps — whatever the configurator is busy
 * with finishes first, then nothing further is fetched or sent. Omit exporterId
 * to cancel whichever export is running.
 */
export function requestExportCancel(exporterId?: string): void {
  const detail: ExportCancelDetail = { exporterId };
  sendEv(window, 'as-export-cancel', { detail });
}

/**
 * Dispatch an as-data-export event on as-app for unified data_export logging.
 * as-app's listener forwards it to sendLog('data_export', subcategory, …). A
 * local save has a clear done signal (saveFileAs resolving), so we log on the
 * confirmed result rather than a started event; a failure folds into the same
 * event via detail.error.
 */
function dispatchDataExport(detail: { subcategory: string; fileInfo?: FileInfoArr; error?: string }): void {
  sendEv(asApp(), 'as-data-export', { detail });
}

export interface DownloadOptions extends ZipExportOptions {
  /**
   * Override the download filename (including extension).
   * Single file default: '{fileName}.{fileType}'.
   * Multi-file ZIP default: '{appSource}.zip'.
   */
  filename?: string;
}

export interface ResolveOptions {
  /**
   * Override the fileType used when fetching mesh parts from the configurator.
   * Useful for formats that require a specific type (e.g. '3mf' for GrabCAD)
   * without permanently mutating the builder's file list.
   */
  meshFileType?: MeshType;
}

/**
 * Descriptor for a modal component associated with an ExportBuilder plugin.
 * Registered via ExportBuilder.registerModal() — consumed by as-app to append elements to its overlay.
 */
export interface ExportModalDescriptor {
  /** Custom element tag name (e.g. 'as-modal-grabcad-success'). */
  tagName: string;
  /** id attribute given to the element when appended to the modal overlay. */
  id: string;
  /** If true, sets disable-bg-close on the element so clicking the overlay backdrop won't dismiss it. */
  disableBgClose?: boolean;
}

/**
 * Descriptor for a button component associated with an ExportBuilder plugin.
 * Registered via ExportBuilder.registerButton() — consumed by as-export-panel.
 */
export interface ExportButtonDescriptor {
  /** Custom element tag name (e.g. 'as-grabcad-export-button'). */
  tagName: string;
  /** as-app[env] value required to show this button. Omit for always-visible buttons. */
  requiredEnv?: string;
  /** Attribute prefix for as-export-panel forwarding (e.g. 'grabcad' makes the panel read 'grabcad-default-text'). */
  panelPrefix?: string;
  /** Additional attribute names forwarded as-is from as-export-panel (e.g. 'package-name'). */
  extraAttrs?: string[];
  /** Static attributes set on the button element by as-export-group (e.g. default-text, tooltip-default-text). */
  defaultAttrs?: Record<string, string>;
}

export class ExportBuilder {
  private static _plugins: Array<(builder: ExportBuilder) => object> = [];
  private static _buttonDescriptors: ExportButtonDescriptor[] = [];
  private static _modalDescriptors: ExportModalDescriptor[] = [];

  /**
   * Register a plugin to be applied to all new ExportBuilder instances.
   */
  static registerPlugin(plugin: (builder: ExportBuilder) => object): void {
    this._plugins.push(plugin);
  }

  /**
   * Register a button component descriptor to be rendered by as-export-panel.
   * Called by plugin modules alongside registerPlugin(). Plugin modules can load
   * and register at any point relative to consumers (as-export-panel/as-export-group
   * connecting, or even as-app itself) depending on a given app's own bundling and
   * module-loading setup — there's no way to guarantee registration always happens
   * before a consumer's first render. Dispatching this event lets consumers
   * re-sync on demand instead of relying on load-order assumptions.
   */
  static registerButton(descriptor: ExportButtonDescriptor): void {
    this._buttonDescriptors.push(descriptor);
    sendEv(window, 'as-export-descriptor-registered', { detail: { kind: 'button' } });
  }

  /** Returns a snapshot of all registered button descriptors. */
  static getButtonDescriptors(): ExportButtonDescriptor[] {
    return [...this._buttonDescriptors];
  }

  /**
   * Register a modal component descriptor to be rendered by as-app inside its modal overlay.
   * Called by plugin modules alongside registerPlugin() / registerButton(). See
   * registerButton()'s doc comment — same reasoning applies to the dispatched event.
   */
  static registerModal(descriptor: ExportModalDescriptor): void {
    this._modalDescriptors.push(descriptor);
    sendEv(window, 'as-export-descriptor-registered', { detail: { kind: 'modal' } });
  }

  /** Returns a snapshot of all registered modal descriptors. */
  static getModalDescriptors(): ExportModalDescriptor[] {
    return [...this._modalDescriptors];
  }

  private _files: FileData[] = [];
  private _beforeExportFns = new Map<FileIdx, () => Promise<void>>();
  private _getDataFns      = new Map<FileIdx, () => Promise<ArrayBuffer>>();
  private _counter = 0;
  private _preferredMeshType: MeshType;
  private _exporterId: string;

  constructor(private _appSource: string, opts: ExportBuilderOptions = {}) {
    this._preferredMeshType = opts.preferredMeshType ?? 'stl';
    this._exporterId        = opts.exporterId        ?? 'default';

    // Apply registered plugins
    for (const plugin of ExportBuilder._plugins) {
      Object.assign(this, plugin(this));
    }
  }

  get exporterId(): string { return this._exporterId; }

  // ── Cancellation ──────────────────────────────────────────────────────────
  //
  // An export is a chain of awaits (configurator reconfigure, mesh fetch, save
  // dialog, GrabCAD transfer). None of those can be torn down mid-flight, so a
  // cancel is co-operative: the flag is set by the as-export-cancel event and
  // checked between steps, which stops everything that hasn't started yet.

  private _cancelled = false;
  private _opDepth = 0;
  /** When the outermost operation started, for the cancel grace period. */
  private _startedAt = 0;
  private _onCancelRequest = (e: Event): void => {
    const id = (e as CustomEvent<ExportCancelDetail>).detail?.exporterId;
    if (id === undefined || id === this._exporterId) this._cancelled = true;
  };

  /** True once a cancel has been requested for the export currently running. */
  get cancelRequested(): boolean { return this._cancelled; }

  /** Throw if the user has cancelled. Call between the async steps of a long export. */
  throwIfCancelled(): void {
    if (this._cancelled) throw new ExportCancelledError();
  }

  /**
   * Hold at the point of no return until the export has been cancellable for
   * CANCEL_GRACE_PERIOD_MS, then throw if a cancel arrived. Call it as the last
   * step before handing data over.
   *
   * Without it, a fast export commits the moment the progress panel appears and
   * Cancel is a button nobody can hit in time. The wait is a real await, so a
   * cancel during it is delivered and honoured — which is the whole point: this
   * is a window in which cancelling works, not a cosmetic delay.
   */
  async awaitCancelGrace(): Promise<void> {
    const remaining = CANCEL_GRACE_PERIOD_MS - (Date.now() - this._startedAt);
    if (remaining > 0) await new Promise<void>(resolve => setTimeout(resolve, remaining));
    this.throwIfCancelled();
  }

  /**
   * Announce that the export has passed the point of no return: the data has
   * left the app and no cancel can call it back. Call it right after the last
   * throwIfCancelled(), before handing the data over.
   *
   * The progress modal drops its Cancel button on this. That matters most where
   * the handover is the slow part — a GrabCAD transfer sits waiting for the host
   * to import, which is exactly when someone gives up and reaches for Cancel.
   * Without this they get a spinner that can't deliver, then a success message
   * for the thing they just tried to stop.
   */
  markUncancellable(): void {
    sendEv(window, 'as-export-uncancellable', {
      detail: { exporterId: this._exporterId }
    });
  }

  /**
   * Run an export operation with cancellation armed: while fn runs, an
   * as-export-cancel event (see requestExportCancel) sets the cancel flag.
   * Nested calls share one listener and one flag, so download() → resolve()
   * arms it once and clears it once, at the end of the outermost call.
   */
  async runCancellable<T>(fn: () => Promise<T>): Promise<T> {
    if (this._opDepth++ === 0) {
      this._cancelled = false;
      this._startedAt = Date.now();
      window.addEventListener('as-export-cancel', this._onCancelRequest);
    }
    try {
      return await fn();
    } finally {
      if (--this._opDepth === 0) window.removeEventListener('as-export-cancel', this._onCancelRequest);
    }
  }

  get preferredMeshType(): MeshType { return this._preferredMeshType; }

  set preferredMeshType(v: MeshType) {
    if (!(MESH_TYPES as readonly string[]).includes(v))
      throw new Error(`[ExportBuilder] invalid preferredMeshType: "${v}" — expected one of: ${MESH_TYPES.join(', ')}`);
    this._preferredMeshType = v;
  }

  /**
   * Clears all files and callbacks from the builder, resetting its state.
   */
  clear(): this {
    this._files = [];
    this._beforeExportFns.clear();
    this._getDataFns.clear();
    this._counter = 0;
    return this;
  }

  private static async _toBuffer(raw: ArrayBuffer | Blob): Promise<ArrayBuffer> {
    return raw instanceof Blob ? raw.arrayBuffer() : raw;
  }

  private _addSchemaItem(input: FileDataSchema, parentIdx?: FileIdx): FileIdx {
    const idx = this._counter++;
    const isMesh = (MESH_CATEGORIES as readonly string[]).includes(input.category);

    let staticData: ArrayBuffer | undefined;
    let dataFn: (() => Promise<ArrayBuffer>) | undefined;

    if (typeof input.data === 'function') {
      const raw = input.data;
      dataFn = async () => ExportBuilder._toBuffer(await raw());
    } else if (input.data instanceof Blob) {
      const blob = input.data;
      dataFn = () => blob.arrayBuffer();
    } else {
      staticData = input.data as ArrayBuffer | undefined;
    }

    this._files.push({
      appSource: this._appSource,
      fileIdx: idx,
      fileKey: input.fileKey ?? crypto.randomUUID(),
      fileName: input.fileName,
      category: input.category,
      fileType: input.fileType ?? (isMesh ? this._preferredMeshType : input.category === 'brep' ? 'stp' : ''),
      data: staticData ?? new ArrayBuffer(0),
      createdAt: new Date(),
      ...(parentIdx !== undefined && { parent: parentIdx }),
      ...(input.finalize !== undefined && { finalize: input.finalize }),
      ...(input.customData && { customData: input.customData }),
      ...(input.tags && { tags: input.tags }),
    } as FileData);

    if (input.beforeExport) this._beforeExportFns.set(idx, input.beforeExport);
    if (dataFn)             this._getDataFns.set(idx, dataFn);

    for (const child of input.children ?? []) {
      this._addSchemaItem(child, idx);
    }

    return idx;
  }

  /**
   * Add any file to the builder. `data` may be a pre-set ArrayBuffer or an async
   * function that returns one at export time (called after beforeExport, if present).
   * For mesh categories without data, the configurator is auto-fetched at resolve time.
   */
  addFile(input: FileDataSchema, parentIdx?: FileIdx): FileIdx {
    return this._addSchemaItem(input, parentIdx);
  }

  /**
   * Shorthand for addFile with category 'trcadMesh'. Data is auto-fetched from
   * as-configurator at resolve time unless overridden by a data function.
   * Supply beforeExport() to set any configurator params needed before the fetch.
   */
  addConfiguratorMesh(input: Omit<FileDataSchema, 'category' | 'fileType'> & { fileType?: MeshType } = {}): FileIdx {
    return this._addSchemaItem({ category: 'trcadMesh', ...input });
  }

  /**
   * Load a declarative schema into the builder.
   * By default clears first; pass { append: true } to extend instead.
   * Handles both PresetFileSchema and AutoFetchSchema items.
   */
  fromSchema(schema: FileDataSchema[], opts: { append?: boolean } = {}): this {
    if (!opts.append) this.clear();
    for (const item of schema) {
      this._addSchemaItem(item);
    }
    return this;
  }

  /**
   * Overrides the fileType for all files in mesh categories.
   */
  setMeshFileType(type: MeshType): this {
    for (const file of this._files) {
      if (isMeshPart(file)) file.fileType = type;
    }
    return this;
  }

  /**
   * Build and export to a zip archive (Uint8Array).
   * For each auto-fetch entry: runs beforeExport() then fetches data via getData() or as-configurator.
   * All fetches run sequentially. No fileType validation — ZIP accepts any format.
   */
  async toZip(opts: ZipExportOptions = {}): Promise<Uint8Array> {
    const resolvedFiles = await this.resolve();
    return buildZipExport(resolvedFiles as FileExportArray, opts);
  }

  /**
   * Resolve and save the result to disk (environment-appropriate, see
   * features/host/fileSave.ts — native save dialog in hosted environments, browser
   * download otherwise).
   * Single file: saves directly with its own filename and extension.
   * Multiple files: packages as a ZIP.
   */
  async download(opts: DownloadOptions = {}): Promise<void> {
    const id = this._exporterId;
    // A host save provider (e.g. GrabCAD's native save dialog) means the file
    // left through that channel, so tag the data_export with the env — mirroring
    // the import side (as-file-input). No provider => a plain browser download,
    // tagged 'local'.
    const subcategory = getFileSaveProvider() ? (internalData.appEnv as string) : 'local';
    dispatchExportStatus({ exporterId: id, operation: 'download', state: 'pending' });
    try {
      await this.runCancellable(async () => {
        const { filename, ...zipOpts } = opts;
        const resolved = await this.resolve();

        let blob: Blob;
        let name: string;

        if (resolved.length === 1) {
          const file = resolved[0];
          blob = new Blob([file.data]);
          // Strip any extension the caller may have supplied (e.g. '.zip') and use the real one.
          const base = filename ? filename.replace(/\.[^.]+$/, '') : (file.fileName ?? file.fileKey);
          name = `${base}.${file.fileType}`;
        } else {
          const zip = await buildZipExport(resolved as FileExportArray, zipOpts);
          blob = new Blob([zip], { type: 'application/zip' });
          name = filename ?? `${this._appSource}.zip`;
        }

        // Last chance to stop: past here the save dialog is the user's own, and
        // it carries its own cancel.
        await this.awaitCancelGrace();
        this.markUncancellable();

        const result = await saveFileAs(blob, name);
        if (!result.success) {
          // User cancelled the save dialog — nothing left the app, so no data_export log.
          dispatchExportStatus({ exporterId: id, operation: 'download', state: 'cancelled' });
          return;
        }

        dispatchExportStatus({ exporterId: id, operation: 'download', state: 'success' });
        // Unified data-export log: a save is a data_export, tagged with the channel
        // it went through. fileInfo carries each resolved part's size + type.
        dispatchDataExport({
          subcategory,
          fileInfo: resolved.map((f) => ({ sizeBytes: f.data.byteLength, fileType: f.fileType })),
        });
      });
    } catch (err: any) {
      if (isExportCancelled(err)) {
        // Nothing was saved, so no data_export log and no error state.
        dispatchExportStatus({ exporterId: id, operation: 'download', state: 'cancelled' });
        return;
      }
      dispatchExportStatus({ exporterId: id, operation: 'download', state: 'error', message: err.message });
      // Fold the failure into the same data_export log via detail.error.
      dispatchDataExport({ subcategory, error: err.message });
      throw err;
    }
  }

  /**
   * Materialise all file data: runs beforeExport hooks, calls data functions, and
   * auto-fetches from as-configurator for trcadMesh / workpiece / brep entries with no data.
   * Sequential — each file resolves before the next begins.
   */
  async resolve(opts: ResolveOptions = {}): Promise<FileData[]> {
    return this.runCancellable(() => this._resolveFiles(opts));
  }

  private async _resolveFiles(opts: ResolveOptions): Promise<FileData[]> {
    const resolved: FileData[] = [];
    const total = this._files.length;
    let filesDone = 0;

    startHistoryBatch('export');
    sendEv(window, 'as-app-busy-request', { detail: { key: 'export', busy: true } });
    dispatchExportProgress({ exporterId: this.exporterId, current: 0, total });
    try {
    for (const file of this._files) {
      // Stop before starting the next file rather than mid-fetch.
      this.throwIfCancelled();
      const fileType = (opts.meshFileType && isMeshPart(file)) ? opts.meshFileType : file.fileType;

      if (file.data.byteLength === 0 && (this._beforeExportFns.has(file.fileIdx) || this._getDataFns.has(file.fileIdx) || isMeshPart(file) || file.category === 'brep')) {
        const beforeExport = this._beforeExportFns.get(file.fileIdx);
        if (beforeExport) await beforeExport();
        // beforeExport reconfigures the model and can take a while — check again
        // so a cancel during it skips the (equally slow) mesh fetch that follows.
        this.throwIfCancelled();

        const getDataFn = this._getDataFns.get(file.fileIdx);
        if (getDataFn) {
          resolved.push({ ...file, fileType, data: await getDataFn() } as FileData);
        } else if (isMeshPart(file) || file.category === 'brep') {
          const cfgr = asConfigurator();
          if (!cfgr)
            throw new Error(
              `[ExportBuilder] as-configurator not found — needed to fetch mesh for "${
                file.fileName ?? file.fileKey
              }"`
            );

          const finalize = file.finalize ?? true;
          const blob = await cfgr.getMesh(fileType, file.fileName ?? file.fileKey, finalize);
          resolved.push({ ...file, fileType, data: await blob.arrayBuffer() } as FileData);
        } else {
          resolved.push({ ...file, fileType, data: file.data.slice(0) } as FileData);
        }
      } else {
        // Slice the ArrayBuffer so postMessage transfers cannot detach _files data,
        // keeping the builder reusable after a GrabCAD send (or any transfer op).
        resolved.push({ ...file, fileType, data: file.data.slice(0) } as FileData);
      }
      dispatchExportProgress({ exporterId: this.exporterId, current: ++filesDone, total });
    }
    } finally {
      cancelHistoryBatch();
      sendEv(window, 'as-app-busy-request', { detail: { key: 'export', busy: false } });
    }

    return resolved;
  }

  /** Return the raw internal file list without resolving data. Useful for inspection; prefer resolve() for export-ready data. */
  build(): FileExportArray {
    return [...this._files] as FileExportArray;
  }

  /** Log and return a human-readable summary of the current file list. */
  print(): string {
    const lines = [`ExportBuilder [${this._appSource}] preferredMeshType=${this._preferredMeshType} — ${this._files.length} file(s)`];
    for (const file of this._files) {
      const parentStr = file.parent !== undefined ? ` → parent:${file.parent}` : '';
      const dataStr    = file.data.byteLength > 0 ? ` [${file.data.byteLength}b]` : ' [auto-fetch]';
      const beforeStr  = this._beforeExportFns.has(file.fileIdx) ? ' +beforeExport' : '';
      const dataFnStr  = this._getDataFns.has(file.fileIdx)      ? ' +data()'       : '';
      lines.push(`  [${file.fileIdx}] ${file.category}/${file.fileType} "${file.fileName ?? file.fileKey}"${parentStr}${dataStr}${beforeStr}${dataFnStr}`);
    }
    const output = lines.join('\n');
    console.log(output);
    return output;
  }
}
